import { useContext, useState } from "react";
import { useHistory, useRouteMatch } from "react-router";

import { BuilderInfoContext, WhenBuilderFlag } from "helpers/globalContext/BuilderInfoContext";
import { usePermission } from "helpers/globalContext/usePermission";

import { BTLocalStorage } from "types/btStorage";

import { externalPages } from "utilities/externalPages";
import { jobDetailRoute } from "utilities/flatRoutes/job.routes";
import { routes } from "utilities/routes";

import { BTAlert } from "commonComponents/btWrappers/BTAlert/BTAlert";
import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { btConfirm } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTExternalLink } from "commonComponents/btWrappers/BTExternalLink/BTExternalLink";
import { BTIconSelections } from "commonComponents/btWrappers/BTIcon";

import {
    builderSupportsTemplateSelectionOptions,
    templateSelectionOptionsParamsFor,
} from "entity/selectionOption/selectionOptionsUtilities";

interface ITryNewSelectionsAlertProps {
    className?: string;
    /**
     * Adds a close button that hides the alert for good, remembered per browser.
     *
     * Off by default, and deliberately so: dismissal is only offered where the invitation is
     * incidental to what the user came to do. On a surface they navigated to on purpose — selections
     * settings — hiding the entry point would strand them with no way to reach the trial. The stored
     * dismissal is scoped to this prop for the same reason: it suppresses the dismissible surface
     * without touching the ones that always show.
     */
    isDismissible?: boolean;
    /**
     * Renders the template wording and points the call to action at a new template instead of a new
     * job. A template's selections page reaches the same alert, and the job copy would promise the
     * wrong thing there.
     */
    isTemplateMode?: boolean;
    /**
     * The builder's setting to put every new job on the new selections experience. When it is on,
     * the alert renders nothing: their next job already uses the experience, so inviting them to
     * trial it on one job would be offering something they have signed up for wholesale.
     *
     * Passed in rather than read here because the two surfaces learn it differently - selections
     * settings has it as a live form value, the selections list gets it with its job config.
     */
    newJobsUseSelectionsReimagined?: boolean;
    /**
     * Runs after the builder confirms, before navigating — use it to persist pending work so it
     * survives leaving the page. Awaited, so an async save completes first.
     *
     * Resolving `false` cancels the navigation, matching what `withFormikBT`'s `handleSubmit`
     * reports when validation blocked the save: staying put keeps the builder with their error
     * instead of silently discarding the edits.
     */
    onBeforeNavigate?: () => unknown;
    /**
     * Replaces the current history entry instead of pushing onto it. Use when this renders inside a
     * route-based modal, so backing out of the new job lands on the parent page rather than
     * reopening the modal.
     */
    replaceHistoryEntry?: boolean;
}

interface IEntityCopy {
    alertTitle?: string;
    lead: string;
    trailer?: string;
    noticeTitle: string;
    noticeMessage: string;
    ctaText: string;
    ctaTestId: string;
    confirmTitle: string;
    confirmContent: string;
    /** Appended to confirmContent only while templates are still cut off from the new experience */
    confirmContentNoTemplateSupport?: string;
    confirmOkText: string;
}

/**
 * Copy and call-to-action wording, which differ only in whether the trial lands on a job or a template
 */
const entityCopy: Record<"job" | "template", IEntityCopy> = {
    job: {
        alertTitle: "Try new selections on your next job",
        lead: "Create your next job with the new experience.",
        trailer: "Existing jobs stay on legacy.",
        noticeTitle: "A new way to manage selections",
        noticeMessage:
            "Your org admin can turn on new selections for future jobs in Selections settings. Existing jobs stay on legacy.",
        ctaText: "Try on a job",
        ctaTestId: "try-on-a-job",
        confirmTitle: "Create job with new selections experience",
        confirmContent: "You cannot switch this job back to legacy selections later.",
        confirmContentNoTemplateSupport:
            " The new experience doesn't connect to templates yet, but has new features for selection creation.",
        confirmOkText: "Create job with new selections",
    },
    template: {
        lead: "Create a new template to try out new selections without impacting existing jobs or templates.",
        noticeTitle: "A new way to manage selections",
        noticeMessage:
            "Your org admin can turn on new selections for future templates in Selections settings. This template stays on legacy.",
        ctaText: "Try on new template",
        ctaTestId: "try-on-a-template",
        confirmTitle: "Create template with new selections experience",
        confirmContent:
            "You cannot switch this template back to legacy selections later. Nothing from the template you're viewing is copied over, you'll start from a blank template.",
        confirmOkText: "Create template with new selections",
    },
} as const;

/**
 * Surfaces the new selections experience on a legacy job or template, adapting to what the viewer
 * can act on: a full admin gets a call to action to trial it on a single new job or template
 * (created with the experience on regardless of their default for new jobs); everyone else gets a
 * read-only notice that it exists and that an admin at their company can turn it on, so they can ask
 * rather than being left unaware.
 *
 * Renders nothing unless the builder has the selectionsReimagined flag, so call sites only need to
 * decide whether their surface is eligible — i.e. that it is not already a reimagined job. The flag
 * lives on the builder account, so a signed-out visitor never reaches either variant.
 */
export function TryNewSelectionsAlert({
    className,
    isDismissible,
    isTemplateMode,
    newJobsUseSelectionsReimagined,
    onBeforeNavigate,
    replaceHistoryEntry,
}: ITryNewSelectionsAlertProps) {
    const history = useHistory();
    // Template details is a relative modal route, so the trial link has to hang off the current page
    const match = useRouteMatch();
    const builderInfo = useContext(BuilderInfoContext);
    const copy = isTemplateMode ? entityCopy.template : entityCopy.job;

    // Same predicate the rest of the trial uses, so the caveat drops exactly when templates can hold options
    const builderSupportsTemplates = builderSupportsTemplateSelectionOptions(
        templateSelectionOptionsParamsFor(builderInfo),
    );
    const confirmContent = builderSupportsTemplates
        ? copy.confirmContent
        : `${copy.confirmContent}${copy.confirmContentNoTemplateSupport ?? ""}`;

    /**
     * Account owners only. All four admin permissions together is what "full admin" means here,
     * mirroring the server's `HasFullAdminAccess()` — opting a job into an experience it can never
     * leave is an account-level decision, not one for a user who merely has selections access.
     */
    const isFullAdmin = usePermission("admin", ["canView", "canAdd", "canUpdate", "canDelete"]);

    // Seeded once from storage so a past dismissal is honored on load, then driven by state so the
    // alert also goes away on the click that stored it.
    const [isDismissed, setIsDismissed] = useState(() =>
        BTLocalStorage.get("bt-boolean-selectionsTrialAlertDismissed"),
    );

    // The account already moving every new job over leaves nothing to invite anyone to, and a stored
    // dismissal hides both variants.
    if (newJobsUseSelectionsReimagined || (isDismissible && isDismissed)) {
        return null;
    }

    const handleDismiss = () => {
        BTLocalStorage.set("bt-boolean-selectionsTrialAlertDismissed", true);
        setIsDismissed(true);
    };

    const handleCreateTrialEntityConfirmed = async () => {
        if (onBeforeNavigate && (await onBeforeNavigate()) === false) {
            return;
        }

        const trialRoute = isTemplateMode
            ? `${match.url}/QuickAction${routes.template.getTemplateDetailsLink(0, {
                  createWithSelectionsReimagined: true,
              })}`
            : jobDetailRoute.link(
                  { jobId: 0 },
                  { openCondensed: true, createWithSelectionsReimagined: true },
              );

        if (replaceHistoryEntry) {
            history.replace(trialRoute);
        } else {
            history.push(trialRoute);
        }
    };

    const handleTryItClick = () => {
        btConfirm({
            title: copy.confirmTitle,
            content: confirmContent,
            okText: copy.confirmOkText,
            onOk: handleCreateTrialEntityConfirmed,
        });
    };

    return (
        <WhenBuilderFlag
            isEnabled="selectionsReimagined"
            noBuilderInfoContextDefault="otherwise"
            then={
                isFullAdmin ? (
                    <BTAlert
                        type="info"
                        className={className}
                        title={copy.alertTitle}
                        message={
                            <>
                                {/* Span so a narrow surface can break after the lead sentence via CSS (see NewJobPrompt.less). */}
                                <span className="try-new-selections-alert--lead">{copy.lead}</span>{" "}
                                {copy.trailer && <>{copy.trailer} </>}
                                <BTExternalLink
                                    href={externalPages.BuildertrendSelectionsSoftware}
                                    data-testid="try-new-selections-learn-more"
                                    isUnderline
                                >
                                    Learn more
                                </BTExternalLink>
                            </>
                        }
                        data-testid="try-new-selections-alert"
                        icon={<BTIconSelections />}
                        showIcon
                        closable={isDismissible}
                        onClose={handleDismiss}
                        action={
                            <BTButton
                                type="secondary"
                                data-testid={copy.ctaTestId}
                                onClick={handleTryItClick}
                            >
                                {copy.ctaText}
                            </BTButton>
                        }
                    />
                ) : (
                    // Non-admins can't switch the account over or create a job, so the only control
                    // is a Learn more link out to the marketing page - it points them at the admin
                    // who can enable it rather than offering a switch they can't use. Rendered in the
                    // action slot as a button (styled anchor) to match the admin variant's layout.
                    <BTAlert
                        type="info"
                        className={className}
                        title={copy.noticeTitle}
                        message={copy.noticeMessage}
                        data-testid="new-selections-available-alert"
                        icon={<BTIconSelections />}
                        showIcon
                        closable={isDismissible}
                        onClose={handleDismiss}
                        action={
                            <BTExternalLink
                                href={externalPages.BuildertrendSelectionsSoftware}
                                data-testid="new-selections-available-learn-more"
                                buttonType="secondary"
                            >
                                Learn more
                            </BTExternalLink>
                        }
                    />
                )
            }
        />
    );
}
